import { create } from "zustand";
import { LocationInput } from "@tripbuilder/cat-accommodation/right/custom/types";

export type ImageData = {
  file: File;
  preview: string;
  url?: string;
};

interface CustomAccommodationState {
  name: string;
  location: LocationInput;
  pricePerNight: string;
  priceType: "perNight" | "total";
  totalPrice: string;
  numNights: string;
  numGuests: string;
  checkInTime: string;
  checkOutTime: string;
  amenities: string[];
  images: ImageData[];
  uploadingImages: boolean;
  currentStepIndex: number;

  // Actions
  setName: (name: string) => void;
  setLocation: (location: LocationInput) => void;
  setPricePerNight: (price: string) => void;
  setPriceType: (type: "perNight" | "total") => void;
  setTotalPrice: (price: string) => void;
  setNumNights: (nights: string) => void;
  setNumGuests: (guests: string) => void;
  setCheckInTime: (time: string) => void;
  setCheckOutTime: (time: string) => void;
  toggleAmenity: (amenity: string) => void;
  setAmenities: (amenities: string[]) => void;
  addImage: (image: ImageData) => void;
  removeImage: (index: number) => void;
  setUploadingImages: (isUploading: boolean) => void;
  setCurrentStepIndex: (index: number) => void;
  reset: () => void;
}

const initialState = {
  name: "",
  location: {
    type: "address" as const,
    value: "",
    coordinates: { latitude: 0, longitude: 0 },
  },
  pricePerNight: "",
  priceType: "perNight" as const,
  totalPrice: "",
  numNights: "",
  numGuests: "",
  checkInTime: "",
  checkOutTime: "",
  amenities: [] as string[],
  images: [] as ImageData[],
  uploadingImages: false,
  currentStepIndex: 0,
};

const useCustomAccommodationStore = create<CustomAccommodationState>((set) => ({
  ...initialState,

  setName: (name) => set({ name }),
  setLocation: (location) => set({ location }),
  setPricePerNight: (pricePerNight) => set({ pricePerNight }),
  setPriceType: (priceType) => set({ priceType }),
  setTotalPrice: (totalPrice) => set({ totalPrice }),
  setNumNights: (numNights) => set({ numNights }),
  setNumGuests: (numGuests) => set({ numGuests }),
  setCheckInTime: (checkInTime) => set({ checkInTime }),
  setCheckOutTime: (checkOutTime) => set({ checkOutTime }),

  toggleAmenity: (amenity) =>
    set((state) => {
      const amenities = state.amenities.includes(amenity)
        ? state.amenities.filter((a) => a !== amenity)
        : [...state.amenities, amenity];

      return { amenities };
    }),

  setAmenities: (amenities) => set({ amenities }),

  addImage: (image) =>
    set((state) => ({
      images: [...state.images, image],
    })),

  removeImage: (index) =>
    set((state) => ({
      images: state.images.filter((_, i) => i !== index),
    })),

  setUploadingImages: (uploadingImages) => set({ uploadingImages }),
  setCurrentStepIndex: (currentStepIndex) => set({ currentStepIndex }),

  reset: () => set(initialState),
}));

export default useCustomAccommodationStore;
